import { emptyValueForSchema, getSchemaAt, CASE_TYPE_SCHEMA } from "../../../../case-spec";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrayShell,
  BoolField,
  EnumSelect,
  MediaUrlField,
  PeopleSelect,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  bool,
  enumOptionsAt,
  isRecord,
  peopleNames,
  str,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

export function PhoneCallsPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const [tab, setTab] = useState<"calls" | "voicemails">("calls");
  const path =
    tab === "calls"
      ? "device.apps.phone.calls"
      : "device.apps.phone.voicemails";
  const items = asArray(getAtPath(root, path));
  const names = peopleNames(root);
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(items.length);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        <Button
          type="button"
          size="sm"
          variant={tab === "calls" ? "secondary" : "ghost"}
          onClick={() => {
            setTab("calls");
            setOpenIndex(null);
          }}
        >
          通话 (
          {asArray(getAtPath(root, "device.apps.phone.calls")).length})
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === "voicemails" ? "secondary" : "ghost"}
          onClick={() => {
            setTab("voicemails");
            setOpenIndex(null);
          }}
        >
          语音信箱 (
          {asArray(getAtPath(root, "device.apps.phone.voicemails")).length})
        </Button>
      </div>

      <ArrayShell
        title={tab === "calls" ? "通话记录" : "语音信箱"}
        count={items.length}
        disabled={disabled}
        onAdd={() => {
          const schema = getSchemaAt(CASE_TYPE_SCHEMA, `${path}[0]`);
          const row = schema
            ? emptyValueForSchema(schema)
            : tab === "calls"
              ? {
                  name: names[0] ?? "",
                  type: "incoming",
                  date: "",
                }
              : {
                  name: names[0] ?? "",
                  initials: "",
                  recorded_at: "",
                  listened: false,
                  audio_url: "",
                };
          onChangeRoot(appendAtPath(root, path, row));
          setOpenIndex(items.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {items.map((item, index) => {
            const rec = isRecord(item) ? item : {};
            const itemPath = childPath(path, index);
            const title =
              tab === "calls"
                ? `${str(rec.name)} · ${str(rec.type)} · ${str(rec.date)}`
                : `${str(rec.name)} · ${str(rec.recorded_at)}`;
            return (
              <RowCard
                key={itemPath}
                title={title || `记录 ${index + 1}`}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={items.length}
                arrayPath={path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                <PeopleSelect
                  label="姓名"
                  value={str(rec.name)}
                  names={names}
                  disabled={disabled}
                  allowEmpty
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "name"), v),
                    )
                  }
                />
                {tab === "calls" ? (
                  <>
                    <EnumSelect
                      label="类型"
                      fieldKey="type"
                      value={str(rec.type)}
                      options={enumOptionsAt(
                        "device.apps.phone.calls[0].type",
                      )}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "type"), v),
                        )
                      }
                    />
                    <TextField
                      label="时间"
                      value={str(rec.date)}
                      disabled={disabled}
                      placeholder="YYYY-MM-DDTHH:mm:ss"
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "date"), v),
                        )
                      }
                    />
                    <TextField
                      label="列表预览"
                      value={str(rec.label)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "label"), v),
                        )
                      }
                    />
                    <TextField
                      label="号码"
                      value={str(rec.number)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "number"), v),
                        )
                      }
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label="缩写"
                      value={str(rec.initials)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "initials"), v),
                        )
                      }
                    />
                    <TextField
                      label="录制时间"
                      value={str(rec.recorded_at)}
                      disabled={disabled}
                      placeholder="YYYY-MM-DDTHH:mm:ss"
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "recorded_at"),
                            v,
                          ),
                        )
                      }
                    />
                    <MediaUrlField
                      label="音频"
                      value={str(rec.audio_url)}
                      disabled={disabled}
                      mediaKind="audio"
                      fieldKey="audio_url"
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "audio_url"), v),
                        )
                      }
                    />
                    <BoolField
                      label="已听"
                      value={bool(rec.listened)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "listened"), v),
                        )
                      }
                    />
                  </>
                )}
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}
